import {
  Box,
  Button,
  Center,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Spacer,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react'
import React from 'react'

interface ConfirmModalProps {
  isOpen: boolean
  isLoading?: boolean

  title: string
  description: string
  icon: React.ReactNode
  onClose: () => void
  onConfirm?: () => void
  onSecondaryAction?: () => void
  secondaryActionText?: string
  children?: React.ReactNode
  hideChildrenSpacer?: boolean
  confirmButtonText?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  finalFocusRef?: React.RefObject<any>
  isSecondaryLoading?: boolean
  showCloseAndContinue?: boolean // Show "Close & Continue in Background" button
  closeOnEsc?: boolean
  closeOnOverlayClick?: boolean
  hideCloseButton?: boolean
  preserveScroll?: boolean
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  icon,
  title,
  confirmButtonText,
  onSecondaryAction,
  secondaryActionText,
  isSecondaryLoading,
  children,
  hideChildrenSpacer,
  description,
  finalFocusRef,
  showCloseAndContinue,
  closeOnEsc = true,
  closeOnOverlayClick = true,
  hideCloseButton = false,
  preserveScroll = false,
}) => {
  const isMobile = useBreakpointValue({ base: true, md: false }, { ssr: false }) ?? false

  const renderActions = () =>
    onConfirm && (
      <Flex
        gap={isMobile ? '10px' : '6px'}
        direction={isMobile ? 'column-reverse' : 'row'}
        p={isMobile ? '4px 20px calc(env(safe-area-inset-bottom, 0px) + 16px)' : '0 24px 16px'}
      >
        {isLoading && showCloseAndContinue ? (
          <Button
            variant="outline"
            w="100%"
            size="lg"
            h={isMobile ? '48px' : '48px'}
            fontSize={isMobile ? '15px' : '16px'}
            borderRadius={isMobile ? '14px' : '8px'}
            sx={{ WebkitTapHighlightColor: 'transparent' }}
            _active={{ transform: 'scale(0.98)' }}
            onClick={onClose}
            colorScheme="primary"
          >
            Close & Continue in Background
          </Button>
        ) : (
          <>
            {onSecondaryAction && (
              <Button
                w="100%"
                size="lg"
                h={isMobile ? '48px' : '48px'}
                fontSize={isMobile ? '15px' : '16px'}
                fontWeight={isMobile ? '600' : undefined}
                px={isMobile ? '12px' : undefined}
                borderRadius={isMobile ? '14px' : '8px'}
                sx={{ WebkitTapHighlightColor: 'transparent' }}
                _active={{ transform: 'scale(0.98)' }}
                onClick={onSecondaryAction}
                isLoading={isSecondaryLoading}
              >
                {secondaryActionText ?? 'Cancel'}
              </Button>
            )}
            <Button
              variant="heavy"
              w="100%"
              size="lg"
              h={isMobile ? '50px' : '48px'}
              fontSize={isMobile ? '15px' : '16px'}
              fontWeight={isMobile ? '600' : undefined}
              px={isMobile ? '12px' : undefined}
              borderRadius={isMobile ? '14px' : '8px'}
              sx={{ WebkitTapHighlightColor: 'transparent' }}
              _active={{ transform: 'scale(0.98)' }}
              onClick={onConfirm}
              isLoading={isLoading}
            >
              {confirmButtonText ?? 'Continue'}
            </Button>
          </>
        )}
      </Flex>
    )

  if (isMobile) {
    return (
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        placement="bottom"
        autoFocus={false}
        closeOnEsc={closeOnEsc}
        closeOnOverlayClick={closeOnOverlayClick}
        blockScrollOnMount={!preserveScroll}
        returnFocusOnClose={!preserveScroll}
        finalFocusRef={finalFocusRef}
      >
        <DrawerOverlay bg="rgba(10, 10, 20, 0.55)" sx={{ backdropFilter: 'blur(6px)' }} />
        <DrawerContent
          bg="white"
          borderTopRadius="24px"
          maxH="90dvh"
          boxShadow="0px -8px 32px -8px rgba(15, 23, 42, 0.18)"
          sx={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          {/* Drag handle */}
          <Center pt="10px" pb="2px" flexShrink={0}>
            <Box w="40px" h="5px" borderRadius="full" bg="neutral.200" />
          </Center>

          <Box flex="1" overflowY="auto" px="20px" pt="14px" pb="4px">
            <Center>
              <Center bg="secondary.200" w="56px" h="56px" borderRadius="14px">
                {icon}
              </Center>
            </Center>
            <Text
              mt="18px"
              fontSize="17px"
              fontWeight="700"
              lineHeight="1.3"
              letterSpacing="-0.01em"
              textAlign="center"
              color="neutral.900"
            >
              {title}
            </Text>
            <Text
              mt="8px"
              fontSize="13px"
              lineHeight="1.5"
              textAlign="center"
              color="neutral.500"
              fontWeight="400"
            >
              {description}
            </Text>
            {children && !hideChildrenSpacer && <Spacer h="20px" />}
            {children}
          </Box>

          {renderActions()}
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      isCentered
      finalFocusRef={finalFocusRef}
      closeOnEsc={closeOnEsc}
      closeOnOverlayClick={closeOnOverlayClick}
      blockScrollOnMount={!preserveScroll}
      returnFocusOnClose={!preserveScroll}
    >
      <ModalOverlay />
      <ModalContent
        mx="auto"
        my="auto"
        maxH="calc(100vh - 120px)"
        borderRadius="12px"
      >
        {!hideCloseButton && <ModalCloseButton />}
        <ModalBody px="24px" py="24px">
          <Center>
            <Center bg="secondary.200" w="56px" h="56px" borderRadius="8px">
              {icon}
            </Center>
          </Center>
          <Text
            mt="20px"
            textStyle="h5"
            textAlign="center"
            color="neutral.900"
          >
            {title}
          </Text>
          <Text
            mt="10px"
            textStyle="b-sm"
            textAlign="center"
            color="neutral.400"
            fontWeight="400"
          >
            {description}
          </Text>
          {children && !hideChildrenSpacer && <Spacer h="40px" />}
          {children}
        </ModalBody>
        {renderActions()}
      </ModalContent>
    </Modal>
  )
}

export default ConfirmModal
